import { FLY_STRIP, LAND_STRIP, TAKEOFF_STRIP } from "./sheet.ts";

/**
 * A flying character's height and the ground shadow under it (P2.22-T38). The strips' art already stands its baked
 * `lift` above the feet line; the flight adds an altitude of its own on top, rising over the take-off, holding with a
 * small bob in the air and falling over the landing. Every number is in the art's pixels, which the page multiplies by
 * the figure's draw scale. A picture only: the true feet and everything on the server stay where the character stands.
 */

/**
 * The altitude held in the air, in art pixels.
 */
export const FLIGHT_CRUISE = 14;

/**
 * How far the cruise altitude bobs up and down, in art pixels.
 */
export const FLIGHT_BOB = 3;

/**
 * The shadow's half width on the ground, in art pixels, at its widest.
 */
export const SHADOW_HALF_WIDEST = 9;

/**
 * The narrowest half width the shadow shrinks to with height, in art pixels.
 */
export const SHADOW_HALF_NARROWEST = 4;

/**
 * Art pixels of height that take one pixel off the shadow's half width.
 */
const SHADOW_SHRINK_PER_PIXEL = 3;

/**
 * The altitude a flight strip adds to its baked lift on one frame, in whole even art pixels so a figure drawn at half
 * size is raised by whole world pixels.
 *
 * @param strip The strip's name.
 * @param frame The frame shown.
 * @param frames The strip's frame count.
 * @returns The altitude: 0 to the cruise over `takeoff`, the cruise with its bob over `fly`, the cruise down to 0 over
 *   `land`, and 0 on any other strip.
 */
export function flightAltitude(strip: string, frame: number, frames: number): number {
  if (strip === TAKEOFF_STRIP) {
    const along = frames > 1 ? frame / (frames - 1) : 1;
    return 2 * Math.floor((FLIGHT_CRUISE * along ** 1.5) / 2);
  }
  if (strip === FLY_STRIP) {
    return 2 * Math.floor((FLIGHT_CRUISE + FLIGHT_BOB * Math.sin(frame / 3)) / 2);
  }
  if (strip === LAND_STRIP) {
    return 2 * Math.floor((FLIGHT_CRUISE * Math.max(0, 1 - frame / 3) ** 1.5) / 2);
  }
  return 0;
}

/**
 * The width of the shadow under a character at a height, in whole world pixels: the widest on the ground, narrower
 * the higher it is, always odd so it centres on the feet.
 *
 * @param height The art's height above the feet line, baked lift and altitude together, in art pixels.
 * @param scale The figure's draw scale.
 * @returns The width in world pixels, at least 3.
 */
export function flightShadowWidth(height: number, scale: number): number {
  const half = Math.max(SHADOW_HALF_NARROWEST, SHADOW_HALF_WIDEST - Math.floor(height / SHADOW_SHRINK_PER_PIXEL));
  const width = Math.round((2 * half + 1) * scale);
  return Math.max(3, width % 2 === 0 ? width - 1 : width);
}
